import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AssetCategory, LocaleCode } from '@/api/models';
import { useFixedAssetQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import { entryWindowForDate } from '../../../registersMeta';
import {
    categoryLabelMap,
    toScheduleRows,
    type ScheduleRow,
} from '../../fixedAssetsMeta';

type UseAssetDetailDrawerParams = {
    /** The asset to show; `null` closes the drawer. */
    assetId: string | null;
    categories: AssetCategory[];
    locale: LocaleCode;
};

/**
 * Logic behind the fixed-asset detail drawer, so the component stays
 * render-only (the repo's triple pattern).
 *
 * Fetches the asset with its depreciation schedule, resolves the category
 * label, and owns the journal drill-through.
 */
export const useAssetDetailDrawer = ({
    assetId,
    categories,
    locale,
}: UseAssetDetailDrawerParams) => {
    const { t } = useTranslation('pages');
    const navigate = useNavigate();

    const query = useFixedAssetQuery(assetId);
    const response = query.data;
    const detail = response?.success ? response.response : null;
    const asset = detail?.asset ?? null;

    const categoryLabel = React.useMemo(
        () =>
            asset
                ? (categoryLabelMap(categories, locale).get(asset.categoryId) ??
                  asset.categoryId)
                : '',
        [asset, categories, locale],
    );

    const scheduleRows = React.useMemo<ScheduleRow[]>(
        () => (detail ? toScheduleRows(detail.schedule, t) : []),
        [detail, t],
    );

    // The Journal **tab**, scoped to the entry's own date. That tab takes its
    // window from the Books shell and cannot widen it, and an acquisition or
    // disposal is dated whenever it happened — rarely the current month.
    const onOpenEntry = React.useCallback(
        (entryId: string, date: string) => {
            const window = entryWindowForDate(date);
            if (!window) {
                return;
            }
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, window),
            );
        },
        [navigate],
    );

    return {
        detail,
        asset,
        categoryLabel,
        scheduleRows,
        isLoading: query.isLoading && assetId !== null,
        onOpenEntry,
    };
};

export type AssetDetailDrawerModel = ReturnType<typeof useAssetDetailDrawer>;
